/* global document */
import { tFunctionType, useI18n } from 'app/components/localization';
import { CALL_UPLOAD_TYPES } from 'app/constants/MediaTypes';
import { checkPayload, SIZE_LIMIT } from 'app/components/forms/File';
import { ChatImageConsumer } from 'app/containers/rtc/ChatImageContext';
import { setTab } from 'app/models/ui/call/actions';
import { CallTab } from 'app/models/ui/call/constants';
import React, { Component, createContext, FunctionComponent, ReactNode, useContext } from 'react';
import { connect } from 'react-redux';
import { AppDispatch } from 'redux';
import { useChatContext } from 'app/components/chat/ChatDocumentContext';

interface IPasteChatImageContext {
  readonly file: File | null;
  readonly uploading: boolean;
  readonly removeFile: PasteChatImageProvider['removeFile'];
  readonly handleUpload: () => void;
}

const PasteChatImageContext = createContext<IPasteChatImageContext>({
  file: null,
  uploading: false,
  removeFile: () => {},
  handleUpload: () => {},
});

export const usePasteChatImageContext = () => useContext(PasteChatImageContext);

interface IProps {
  readonly children: ReactNode;
}

interface IDeps {
  readonly t: tFunctionType;
  readonly upload: (file: File) => Promise<void>;
  readonly sendImageError: (error: Error) => void;
  readonly resetTab: () => void;
  readonly disabled: boolean;
}

interface IState {
  readonly file: File | null;
  readonly uploading: boolean;
}

export class PasteChatImageProvider extends Component<IProps & IDeps, IState> {
  public readonly state: IState = {
    file: null,
    uploading: false,
  };

  public componentDidMount() {
    document.addEventListener('paste', this.handleFilePaste, true);
  }

  public componentWillUnmount() {
    document.removeEventListener('paste', this.handleFilePaste, true);
  }

  public render() {
    return (
      <PasteChatImageContext.Provider
        value={{
          ...this.state,
          removeFile: this.removeFile,
          handleUpload: this.handleUpload,
        }}
      >
        {this.props.children}
      </PasteChatImageContext.Provider>
    );
  }

  private removeFile = () => {
    if (!this.state.uploading) {
      this.forceRemoveFile();
    }
  };

  private forceRemoveFile = () => {
    this.setState({ file: null, uploading: false });
  };

  private handleFilePaste = (e: ClipboardEvent) => {
    if (!this.state.uploading && !this.props.disabled
        && e.clipboardData && e.clipboardData.files && e.clipboardData.files.length > 0) {
      this.setState({ file: e.clipboardData.files[0], uploading: false });
    }
  };

  private handleUpload = async () => {
    const { t, upload, sendImageError, resetTab } = this.props;
    const { file, uploading } = this.state;

    if (uploading || !file) {
      return;
    }

    this.setState({ file, uploading: true });

    try {
      resetTab();
      const result = checkPayload(file, SIZE_LIMIT, CALL_UPLOAD_TYPES, t);
      await upload(result);
    } catch (error) {
      sendImageError(error);
    } finally {
      this.forceRemoveFile();
    }
  };
}

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  resetTab: () => dispatch(setTab(CallTab.Chat)),
});

const ConnectedPasteChatImageProvider = connect(
  null,
  mapDispatchToProps,
)(PasteChatImageProvider);

export const PasteChatImageProviderInjectDeps: FunctionComponent = ({
  children,
}) => {
  const { t } = useI18n();
  const { isBlocked } = useChatContext();

  return (
    <ChatImageConsumer>{({ upload, sendImageError }) => (
      <ConnectedPasteChatImageProvider
        t={t}
        upload={upload}
        sendImageError={sendImageError}
        disabled={isBlocked}
      >
        {children}
      </ConnectedPasteChatImageProvider>
    )}</ChatImageConsumer>
  );
};

export const PasteChatImageConsumer = PasteChatImageContext.Consumer;
